import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { MotionConfig } from "motion/react";
import { useAgentFocus } from "@/app/routes/dashboard/agent/agentFocusStore";
import DashboardPage from "@/components/DashboardPage";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/utils/helpers";
import type { Trend, TrendType } from "@/types/trend.type";
import {
  isSemanticQuery,
  trendAxis,
  trendFanoutPart,
  trendMerge,
  trendSingleView,
  trendWallPlan,
  useTrendPages,
  useTrends,
  useTrendsFanout,
  type TrendFeedState,
  type TrendFilters,
  type TrendPlatform,
  type TrendSort,
} from "./api/useTrends";
import { mergedWallView, type WallView } from "@/app/routes/dashboard/components/wallFanout";
import TrendCard, { type TrendPlateSize } from "./components/TrendCard";
import TrendDetailModal, { type TrendDetailNav, type TrendDetailPreview } from "./components/TrendDetailModal";
import { useCalendarPillars } from "@/app/routes/dashboard/content-calendar/api/useContentCalendar";
import { buildPillarHueMap, pillarUiTone } from "@/app/routes/dashboard/strategy/components/strategyMeta";
import TrendFilterPanel from "@/app/routes/dashboard/components/WallFilterPanel";
import TrendsScrolledRow from "@/app/routes/dashboard/components/WallScrolledRow";
import TrendsPhoneFeed from "./phone/TrendsPhoneFeed";
import type { PlateArt } from "@/app/routes/dashboard/components/Plate";
import { PlaybackProvider } from "@/app/routes/dashboard/components/mediaPlayback";
import { Pill } from "@/app/routes/dashboard/components/ds";
import { EmptyState, ErrorState, FilteredEmptyState } from "@/app/routes/dashboard/components/ViewStates";
import { Inbox } from "lucide-react";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useTrendsLastSeen } from "./useTrendsLastSeen";
import {
  WallBar,
  WallCaption,
  WallFacet,
  WallFilterAnnouncer,
  WallHairline,
  WallIncompleteNotice,
  WallRail,
  WallSearch,
  WallPager,
  WallViewHeader,
  type WallFacetOption,
  type WallRailOption,
} from "@/app/routes/dashboard/components/WallBar";
import { platformLabel, sinceLabel, topPillarFit, TYPE_LABEL, TYPE_OPTIONS } from "./components/trendMeta";
import {
  ANY_REGION,
  anyFilterSet,
  REGION_OPTIONS,
  regionWord,
  SEARCH_ORDER_LABEL,
  SORT_OPTIONS,
  sortFaceLabel,
  SOURCE_OPTIONS,
  trendFilterGroups,
  trendFilterSummary,
  useTrendFilters,
} from "./trendFilters";
import "./trendWall.css";

/**
 * Trends: the video wall.
 *
 * Four things this page is built to never say, because the data cannot support
 * any of them:
 *
 *  - That anything is rising, peaking, accelerating or cooling. Nothing measures
 *    a trend continuously, and the one stored score that orders by something
 *    like momentum is never sent to the client and cannot be explained here, so
 *    the page neither draws it nor sorts by it. No sparkline, no delta, no rank
 *    move.
 *  - That a figure is settled. A count is a snapshot from the discovery run that
 *    last saw the trend; a later run that re-identifies it appends a fresh
 *    observation and the feed reads that one instead.
 *  - That a figure is exact, or the viewer's. Every view and like belongs to the
 *    creators of the harvested example videos, and an example whose count never
 *    came back adds nothing to a total, so a total is a floor.
 *  - That a trend is matched to, meant for, or relevant to a brand. A Trend has
 *    no brandId by design; region and language are filters over where the
 *    example videos were found, and nothing more.
 *
 * THE ORDER IS A CHOICE, AND A PLAIN ONE (2026-09-26). Sort offers Newest, Most
 * viewed and Most liked. Views and likes are floors from a snapshot, and every
 * card already prints them that way, with a "+": sorting by them orders what is
 * shown by those same floors and claims nothing more than the cards do. It is
 * not a ranking of how a trend is doing. Newest is the default and `sort` is
 * always sent EXPLICITLY, because the route's unnamed default orders by a
 * momentum score that never reaches the client and cannot be explained here.
 * Each order is one the client can reproduce when it has to merge several
 * answers into one wall (trendMerge).
 *
 * A SEARCH IS THE ONE EXCEPTION, and it is not a second sort control: nobody picks
 * it, typing a query IS it. `q` puts the route on its vector path, which orders by
 * cosine relevance and ignores `sort`, so the wall sends no `sort` while searching,
 * re-sorts a merged search by the `similarity` the route returns on each row, and the
 * Sort facet reads "Best match" and cannot be changed. Every claim above still holds: relevance
 * is a distance between what was typed and what the trend says it is, which is a
 * property of the query, not a measurement of the trend.
 */

/** The view's one line, under the title. It carries the two things the rest of
 *  the page spends its surface qualifying: where these came from, and whose
 *  numbers the figures are. Reword it here; nothing else prints it. */
const SUBHEAD = "What the discovery runs found on TikTok and Instagram. The counts are the creators'.";

const PAGE_SIZE = 24;

/** Under this the page is the phone feed, one trend per screen. */
const PHONE_QUERY = "(max-width: 767px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
/** How many example clips each phone feed row carries, for the swipe. */
const PHONE_PREVIEW_COUNT = 6;

/** A stand-in for a count request that is not being made. Disabled, so it is
 *  never sent; stable, so the hooks do not see a new question every render. */
const NO_REQUEST: TrendFilters = {};
const NO_REQUESTS: TrendFilters[] = [];

/** The ways a person moves the page themselves. Any of them after a close
 *  means the late restore pass would be fighting them, so it stands down. */
const USER_INPUT_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"] as const;

/**
 * Bring a card into the field's view, but only when it is wholly out of it:
 * a card with any part showing below the sticky bar is left exactly where it
 * is. Lands the card just under the bar, in whichever direction it was.
 */
function revealIfOffscreen(field: HTMLElement, card: HTMLElement, root: HTMLElement | null): void {
  const view = field.getBoundingClientRect();
  const box = card.getBoundingClientRect();
  const bar = root?.querySelector(".wb-sticky")?.getBoundingClientRect().height ?? 0;
  const top = view.top + bar;
  if (box.bottom > top && box.top < view.bottom) return;
  field.scrollTop += box.top - top - 16;
}

/** Plate size is a layout rhythm, not a reading of the data. Both steps are
 *  portrait (9:16 and 3:4) because the source is Reels and TikToks; only the
 *  height alternates. The one size that carries meaning is the double-width
 *  plate, and that one is boosted, which the plate also says in words. */
const SIZE_RHYTHM: TrendPlateSize[] = ["tall", "short", "tall", "short", "short", "tall", "tall", "short"];

/* REGION is ONE control, not two. A discovery run plans region and language as a
   pair, so two independent facets would let a viewer ask for MENA + English,
   which can only ever answer zero. The rows say MENA and Global; underneath they
   are still the planned pairs (TREND_AXES). Nothing picked filters on neither,
   which is how a trend tagged outside the two planned libraries stays
   reachable, and that is what the Any region row is. */
const REGION_FACET_OPTIONS: WallFacetOption<(typeof REGION_OPTIONS)[number]["value"]>[] =
  REGION_OPTIONS.map((option) => ({ value: option.value, label: option.label, icon: option.icon }));

const SORT_FACET_OPTIONS: WallFacetOption<TrendSort>[] = SORT_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const KIND_OPTIONS: WallRailOption<TrendType>[] = TYPE_OPTIONS.map((t) => ({
  value: t,
  label: TYPE_LABEL[t],
  text: TYPE_LABEL[t],
}));

/* The mark rides WITH the word, never instead of it. */
const SOURCE_RAIL_OPTIONS: WallRailOption<TrendPlatform>[] = SOURCE_OPTIONS.map((option) => ({
  value: option.value,
  label: (
    <>
      <option.icon />
      {option.label}
    </>
  ),
  text: option.label,
}));

/** How the live region says the order, in its own sentence. */
const ORDER_SPOKEN: Record<TrendSort, string> = {
  newest: "newest first",
  views: "most viewed first",
  likes: "most liked first",
};

function plateSize(trend: Trend, index: number): TrendPlateSize {
  return trend.isBoosted ? "wide" : SIZE_RHYTHM[index % SIZE_RHYTHM.length];
}

export interface TrendsPreview {
  feed: (filters: TrendFilters) => TrendFeedState;
  /** What the wall is about to ask for, in full, once per render.
   *
   *  The harness is handed ONE request at a time and cannot tell where one
   *  render's batch ends and the next begins, so it cannot count them. This
   *  hands it the whole plan, which is the same array the live path gives
   *  `useQueries` — so a harness state can report exactly how many requests the
   *  live wall issues, on a route that deliberately issues none. */
  onPlan?: (requests: readonly TrendFilters[]) => void;
  detail: (id: string) => TrendDetailPreview;
  standInFor: (trend: Trend) => PlateArt | undefined;
}

function SkeletonWall() {
  return (
    /* The field is what the wall measures itself against, so the skeleton needs
       it too: without it the column arithmetic has no container to read. */
    <div className="co-wall-field">
      <div className="co-wall" aria-hidden="true">
        {SIZE_RHYTHM.concat(SIZE_RHYTHM).map((size, i) => (
          <div
            key={`${size}-${i}`}
            className={cn(
              "trend-cell",
              size === "tall" && "trend-cell--tall",
              size === "short" && "trend-cell--short",
            )}
          >
            {/* The real card's height comes from the 9:16 media pane plus its
                label block, so the ghost is built from the same two rows: a bare
                skeleton block here has no height source and collapses flat. */}
            <div className="trend-plate">
              <div className="trend-media co-wall-media">
                <div className="co-skeleton absolute inset-0" />
              </div>
              {/* One pulse for the whole ghost: the bars take the same shared
                  recipe as the frame (co-skeleton, wall.css), so they breathe
                  in step with it instead of on a second clock. */}
              <div className="trend-info">
                <div className="co-skeleton h-3 w-2/5" />
                <div className="co-skeleton mt-2.5 h-5 w-4/5" />
                <div className="co-skeleton mt-3 h-3 w-1/2" />
                <div className="co-skeleton mt-1.5 h-3 w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TrendsPage({ preview }: { preview?: TrendsPreview }) {
  useAgentFocus({ surface: "trends" });
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [page, setPage] = useState(1);
  /* The phone feed reads pages 1 to N and asks for the next as it nears the
     end, where the desktop wall shows one page at a time. */
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [phonePages, setPhonePages] = useState(1);
  /* Every filter, in one state the bar, the scrolled panel and the phone sheet
     all read and write (trendFilters.ts). Any change goes back to page one: a
     new question starts from the top of its answer. */
  const toFirstPage = useCallback(() => {
    setPage(1);
    setPhonePages(1);
  }, []);
  const { state: filterState, actions: filterActions } = useTrendFilters(toFirstPage);
  const { types, platforms, region: axisKey, sort } = filterState;
  const searching = isSemanticQuery(submittedQ);
  /* The open panel is the URL, not local state: refresh, a shared link and the
     Home rail's title all land on the same sheet. `?trend=` matches how
     universe (`?fact`) and strategy (`?strategy`) already address a record. */
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTrendId = searchParams.get("trend");

  const selectTrend = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set("trend", id);
          else next.delete("trend");
          return next;
        },
        /* Close replaces so the entry that opened the panel is not left behind
           to reopen it on Back. Open pushes so Back still closes it. */
        { replace: id == null },
      );
    },
    [setSearchParams],
  );

  /* THE COCKPIT'S SCROLL FIELD. The page does not scroll itself: CockpitLayout's
     .co-grid-field is the one scroller every view sits in (and the preview
     harness gives the page the same element), and the sticky bar sticks to it.
     Found from the page's own root rather than threaded through the layout, so
     no second scroller is introduced and nothing about the shell changes. */
  const rootRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLElement | null>(null);
  /* Found again whenever the layout swaps: the phone feed does not carry
     rootRef, so a page first drawn at phone width has no field to find yet. */
  useLayoutEffect(() => {
    fieldRef.current =
      rootRef.current?.closest<HTMLElement>(".co-grid-field") ??
      (document.scrollingElement as HTMLElement | null);
  }, [isPhone]);

  /* KEEP YOUR PLACE (2026-09-26). Once the view header has scrolled wholly out
     of the field, the bar hands its slot to the scrolled row: the view's name,
     a FILTERS facet and the search, in thick glass. Watched with an observer on
     the header against the field itself, so the switch lands exactly where the
     bar starts to stick, whatever height the header wraps to. */
  /* WHERE FOCUS GOES WHEN THE WALL IS REPLACED. A page turn or Clear filters
     swaps the wall for its skeleton, and the button that was pressed goes with
     it, which used to drop keyboard focus onto the body. The wall's heading is
     never unmounted, so focus moves there, and the field brings it into view
     under the sticky bar if it is out of sight: smoothly, unless reduced
     motion is asked for. */
  const wallHeadingRef = useRef<HTMLHeadingElement>(null);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const focusWallTop = useCallback(() => {
    const heading = wallHeadingRef.current;
    if (!heading) return;
    heading.focus({ preventScroll: true });
    const field = fieldRef.current;
    if (!field) return;
    const fieldTop = field === document.scrollingElement ? 0 : field.getBoundingClientRect().top;
    const fieldBottom =
      field === document.scrollingElement ? window.innerHeight : field.getBoundingClientRect().bottom;
    const bar = rootRef.current?.querySelector(".wb-sticky")?.getBoundingClientRect().height ?? 0;
    const top = heading.getBoundingClientRect().top;
    if (top >= fieldTop + bar && top < fieldBottom) return;
    field.scrollTo({
      top: Math.max(0, field.scrollTop + top - fieldTop - bar - 16),
      behavior: reducedMotion ? "instant" : "smooth",
    });
  }, [reducedMotion]);

  const [pastHeader, setPastHeader] = useState(false);
  /* Watches the header the desktop layout has mounted NOW. Zooming to a phone
     width swaps the header for the feed and back again, and an observer left on
     the old, detached header reads it as scrolled away for good: the wall came
     back stuck in its scrolled row with the full bar hidden. */
  useEffect(() => {
    if (isPhone) return;
    const header = rootRef.current?.querySelector(".wb-head");
    const field = fieldRef.current;
    if (!header || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => setPastHeader(entries.every((entry) => !entry.isIntersecting)),
      { root: field && field !== document.scrollingElement ? field : null, threshold: 0 },
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, [isPhone]);

  /* BACK TO THE CARD. Opening remembers which card and where the field was;
     closing, by the panel's button, Escape or the browser's Back, puts focus
     back on the card of the trend showing at that moment, without scrolling.
     If the panel never moved, the field goes back exactly where it was. If J or
     K moved it to another trend, the field stays put and that trend's card is
     only brought into view when it is wholly off screen. The filters, the
     search, the page and the feed are state here and in the query cache, and
     opening a panel touches none of them. */
  const returnTo = useRef<{ id: string; top: number; moved: boolean } | null>(null);
  const openTrend = useCallback(
    (id: string) => {
      returnTo.current = { id, top: fieldRef.current?.scrollTop ?? 0, moved: false };
      selectTrend(id);
    },
    [selectTrend],
  );
  /* A move inside the open panel. It REPLACES the history entry, so Back still
     closes the panel rather than stepping back through every trend visited. */
  const moveToTrend = useCallback(
    (id: string) => {
      const back = returnTo.current;
      returnTo.current = back
        ? { ...back, id, moved: true }
        : { id, top: fieldRef.current?.scrollTop ?? 0, moved: true };
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("trend", id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  useEffect(() => {
    if (selectedTrendId !== null) return;
    const back = returnTo.current;
    if (!back) return;
    returnTo.current = null;
    const field = fieldRef.current;

    /* Anything the person does themselves after the close. The scroll check
       only counts a position this code did not set. */
    let interacted = false;
    let expectedTop: number | null = null;
    const noteInput = () => {
      interacted = true;
    };
    const noteScroll = () => {
      if (field && expectedTop !== null && Math.abs(field.scrollTop - expectedTop) > 1) interacted = true;
    };
    USER_INPUT_EVENTS.forEach((type) =>
      window.addEventListener(type, noteInput, { capture: true, passive: true }),
    );
    field?.addEventListener("scroll", noteScroll, { passive: true });
    const stopListening = () => {
      USER_INPUT_EVENTS.forEach((type) => window.removeEventListener(type, noteInput, { capture: true }));
      field?.removeEventListener("scroll", noteScroll);
    };

    const restore = () => {
      const hit = rootRef.current?.querySelector<HTMLElement>(
        `[data-trend-id="${CSS.escape(back.id)}"] .trend-hit`,
      );
      if (hit && document.activeElement !== hit) hit.focus({ preventScroll: true });
      if (!field) return;
      if (!back.moved) {
        if (field.scrollTop !== back.top) field.scrollTop = back.top;
      } else if (hit) {
        revealIfOffscreen(field, hit.closest<HTMLElement>(".trend-cell") ?? hit, rootRef.current);
      }
      expectedTop = field.scrollTop;
    };
    /* Twice: once as soon as the panel is off the page, and once after its exit
       animation (about 100ms) and its focus scope's own restore have both run,
       so neither can leave focus or the scroll somewhere else. The second pass
       is skipped if the person has scrolled, wheeled, touched or pressed a key
       since the close: by then the page is theirs again. */
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(restore);
    });
    const settle = window.setTimeout(() => {
      if (!interacted) restore();
      stopListening();
    }, 180);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      stopListening();
    };
  }, [selectedTrendId]);

  const axis = trendAxis(axisKey);

  /* WHAT THE ROUTE CAN ANSWER, AND HOW A SET IS ASKED ANYWAY.

     `v_FeedQuery` types `type` as a single string and the repository writes it
     as `t.type = '<value>'`; `platform` is a two-value zod enum, so a
     comma-joined list does not narrow, it 400s. Neither axis takes a set.

     So a set is asked ONE VALUE AT A TIME. The plan is the cross product of the
     two axes — `(format OR topic) AND (tiktok OR instagram)` is four exact
     server-side reads — and every one of them answers over the WHOLE library, so
     the merged wall is a whole-library answer too. Nothing is narrowed on the
     client any more. That is the bug this replaces: the old path filtered the
     rows ONE request had already returned, so a match sitting on page two was
     invisible and the count could FALL when a second value widened the question.

     A product of one is the single-request path, byte for byte what it was: one
     request, the viewer's page, exactly what the route answered.

     What a fan-out cannot do is line the constituents' pages up with each other,
     so each reads one window at the route's maximum page size and the merged set
     is paged on the client. A value holding more than that window is read short,
     which is the ONE condition under which this wall is still incomplete. The
     wall prints no count, so there is no total left to overstate. */
  const feedBase = useMemo(
    () => ({
      q: submittedQ || undefined,
      // Region and language go together or not at all. A discovery run plans
      // two libraries, MENA in Arabic and global in English, so sending one
      // half of a pair without the other asks for a combination no run
      // produces and which can therefore only answer zero.
      region: axis.region,
      language: axis.language,
      /* Sent, not omitted, Newest included: the route's unnamed default is a
         momentum order the client never sees. NOT sent under a search: that
         path orders by cosine relevance and drops `sort` on the floor, so
         sending it would put a parameter in the query string that the answer
         does not reflect. */
      sort: searching ? undefined : sort,
    }),
    [submittedQ, axis.region, axis.language, searching, sort],
  );
  const plan = useMemo(
    () => trendWallPlan(feedBase, types, platforms, page, PAGE_SIZE),
    [feedBase, types, platforms, page],
  );
  const fanned = plan.requests.length > 1;

  /* Both hooks are called and exactly one is enabled, because hooks cannot be
     conditional and a disabled query issues no request. */
  const single = useTrends(plan.requests[0], { enabled: !preview && !fanned && !isPhone });
  const merged = useTrendsFanout(plan.requests, {
    enabled: !preview && fanned && !isPhone,
    capped: plan.capped,
  });

  /* One shape for the whole render tree, whichever path answered. The preview
     harness answers one request at a time in the same shape the live hook does,
     so /design/trends-preview exercises the real merge rather than a second
     implementation of it. */
  const view: WallView<Trend> = useMemo(() => {
    if (preview) {
      preview.onPlan?.(plan.requests);
      const answers = plan.requests.map((request) => preview.feed(request));
      if (!fanned) return trendSingleView(answers[0]);
      return mergedWallView(
        /* The same merge the live hook uses, off the same plan, so the preview
           harness exercises the real ordering rather than a second one. */
        trendMerge(
          answers.map((answer, i) => trendFanoutPart(answer, plan.requests[i])),
          plan.requests,
          plan.capped,
        ),
        page,
        PAGE_SIZE,
        plan.requests.length,
      );
    }
    if (!fanned) return trendSingleView(single);
    return mergedWallView(merged, page, PAGE_SIZE, plan.requests.length);
  }, [preview, plan, fanned, page, single, merged]);

  const { rows: visible, pageCount, isLoading, isFetching, isError, refetch } = view;

  /* NEW SINCE YOU LAST LOOKED. The time of the previous visit is kept in this
     browser per brand; a first visit has none and marks nothing.

     THE COUNT IS THE ROUTE'S, NOT THE PAGE'S. The feed takes `createdAfter`
     and answers with `total`, so the figure is asked for directly: the SAME
     question the wall is asking (the same filters, search and fan-out, through
     the same plan builder) with `createdAfter` set to the last visit and a page
     of one row, because only the total is read. It therefore counts every
     matching trend that arrived since, on every page, not just the 24 loaded
     here. A fan-out reads its windows like the wall does, and a window that
     came back short makes the figure a floor, printed with a "+". While it is
     being read, or if it fails, nothing is printed rather than a guess. */
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const lastSeen = useTrendsLastSeen(brandId);
  const lastSeenMs = lastSeen ? Date.parse(lastSeen) : null;
  const freshPlan = useMemo(
    () =>
      lastSeen
        ? trendWallPlan({ ...feedBase, createdAfter: lastSeen }, types, platforms, 1, 1)
        : null,
    [lastSeen, feedBase, types, platforms],
  );
  const freshFanned = (freshPlan?.requests.length ?? 0) > 1;
  const freshSingle = useTrends(freshPlan?.requests[0] ?? NO_REQUEST, {
    enabled: !preview && !isPhone && freshPlan !== null && !freshFanned,
  });
  const freshMerged = useTrendsFanout(freshFanned && freshPlan ? freshPlan.requests : NO_REQUESTS, {
    enabled: !preview && !isPhone && freshFanned,
    capped: freshPlan?.capped ?? false,
  });

  /* THE PHONE FEED'S ROWS: the same question with up to six example clips per
     row. One request pages 1 to N, each page its own cache entry; a fan-out
     reads its windows as the wall does and the feed shows them N pages' worth
     at a time. Only asked for on a phone, where the wall's own reads are off. */
  const phoneBase = useMemo(() => ({ ...feedBase, previewCount: PHONE_PREVIEW_COUNT }), [feedBase]);
  const phoneFirst = useMemo(
    () => trendWallPlan(phoneBase, types, platforms, 1, PAGE_SIZE),
    [phoneBase, types, platforms],
  );
  const phoneFanned = phoneFirst.requests.length > 1;
  const phonePageRequests = useMemo(
    () =>
      phoneFanned
        ? NO_REQUESTS
        : Array.from(
            { length: phonePages },
            (_, i) => trendWallPlan(phoneBase, types, platforms, i + 1, PAGE_SIZE).requests[0],
          ),
    [phoneFanned, phonePages, phoneBase, types, platforms],
  );
  const phoneSingle = useTrendPages(phonePageRequests, {
    enabled: isPhone && !preview && !phoneFanned,
  });
  const phoneMerged = useTrendsFanout(phoneFanned ? phoneFirst.requests : NO_REQUESTS, {
    enabled: isPhone && !preview && phoneFanned,
    capped: phoneFirst.capped,
  });
  const phone = (() => {
    const shown = phonePages * PAGE_SIZE;
    if (preview) {
      if (phoneFanned) {
        const answer = trendMerge(
          phoneFirst.requests.map((request) => trendFanoutPart(preview.feed(request), request)),
          phoneFirst.requests,
          phoneFirst.capped,
        );
        return {
          rows: answer.rows.slice(0, shown),
          hasMore: answer.rows.length > shown,
          isLoading: answer.isLoading,
          isFetchingMore: false,
          isError: answer.isError,
          refetch: answer.refetch,
          failed: answer.failed,
          requested: phoneFirst.requests.length,
          moreError: false,
          retryMore: () => {},
        };
      }
      const answers = phonePageRequests.map((request) => preview.feed(request));
      const last = answers[answers.length - 1];
      return {
        rows: answers.flatMap((answer) => answer.trends),
        hasMore: Boolean(last && last.nextPage !== null),
        isLoading: answers[0]?.isLoading ?? false,
        isFetchingMore: false,
        isError: answers[0]?.isError ?? false,
        refetch: () => {},
        failed: 0,
        requested: 1,
        moreError: false,
        retryMore: () => {},
      };
    }
    if (phoneFanned) {
      return {
        rows: phoneMerged.rows.slice(0, shown),
        hasMore: phoneMerged.rows.length > shown,
        isLoading: phoneMerged.isLoading,
        isFetchingMore: false,
        isError: phoneMerged.isError,
        refetch: phoneMerged.refetch,
        /* Some of the merged reads did not answer while others did: the feed
           says so, as the wall does, rather than looking complete. */
        failed: phoneMerged.failed,
        requested: phoneFirst.requests.length,
        moreError: false,
        retryMore: () => {},
      };
    }
    return { ...phoneSingle, failed: 0, requested: 1 };
  })();
  const loadMorePhone = useCallback(() => setPhonePages((n) => n + 1), []);

  /* MOVE THROUGH TRENDS IN THE DETAIL, across the trends loaded on this page
     in wall order. A trend opened from somewhere else (a shared link, Home)
     that is not on this page gets no position and no moves. */
  const navIndex = selectedTrendId === null ? -1 : visible.findIndex((trend) => trend.id === selectedTrendId);
  const detailNav = useMemo<TrendDetailNav | undefined>(
    () =>
      navIndex < 0
        ? undefined
        : {
            index: navIndex,
            count: visible.length,
            onPrevious: navIndex > 0 ? () => moveToTrend(visible[navIndex - 1].id) : null,
            onNext: navIndex < visible.length - 1 ? () => moveToTrend(visible[navIndex + 1].id) : null,
          },
    [navIndex, visible, moveToTrend],
  );

  /* PILLAR FIT, which ships dark: nothing sends `pillarFit` yet, so there are no
     fits, no pillar request and nothing drawn. When it arrives, each fitted
     pillar takes the hue the brand's strategy gives it everywhere else, and
     the brand's pillars are only read while some card on the page has a fit.
     Backend request: https://github.com/Mo7tawa/creative-os-backend/issues/91 */
  const fitPillarIds = useMemo(
    () =>
      visible.flatMap((trend) => {
        const fit = topPillarFit(trend);
        return fit ? [fit.pillarId] : [];
      }),
    [visible],
  );
  const fitPillars = useCalendarPillars(!preview && !isPhone && fitPillarIds.length > 0 ? brandId : null);
  const fitHues = useMemo(() => {
    const pillars = fitPillars.data?.pillars;
    if (!pillars || fitPillarIds.length === 0) return null;
    return buildPillarHueMap(pillars, fitPillarIds);
  }, [fitPillars.data, fitPillarIds]);
  const fitHueOf = (trend: Trend): string | undefined => {
    const fit = topPillarFit(trend);
    const hue = fit ? fitHues?.get(fit.pillarId) : undefined;
    return hue ? pillarUiTone(hue) : undefined;
  };

  /* The scrolled row stays while its search holds focus, even scrolled back to
     the top, so the field is never pulled out from under somebody typing. */
  const [rowSearchFocused, setRowSearchFocused] = useState(false);
  /* A layout swap starts the desktop wall from its top state. Neither flag can
     clear itself across one: the header that set pastHeader is gone, and an
     unmounted search never fires the blur that clears rowSearchFocused. */
  const [layoutIsPhone, setLayoutIsPhone] = useState(isPhone);
  if (layoutIsPhone !== isPhone) {
    setLayoutIsPhone(isPhone);
    setPastHeader(false);
    setRowSearchFocused(false);
  }
  const condensedActive = pastHeader || rowSearchFocused;

  let fresh: { count: number; atLeast: boolean } | null = null;
  if (freshPlan && preview) {
    const answers = freshPlan.requests.map((request) => preview.feed(request));
    if (!freshFanned) {
      const answer = answers[0];
      if (!answer.isLoading && !answer.isError) fresh = { count: answer.total, atLeast: false };
    } else {
      const answer = trendMerge(
        answers.map((a, i) => trendFanoutPart(a, freshPlan.requests[i])),
        freshPlan.requests,
        freshPlan.capped,
      );
      if (!answer.isLoading && !answer.isError) {
        fresh = { count: answer.count, atLeast: answer.atLeast || answer.failed > 0 };
      }
    }
  } else if (freshPlan && !freshFanned) {
    if (!freshSingle.isLoading && !freshSingle.isError && !freshSingle.isPlaceholder) {
      fresh = { count: freshSingle.total, atLeast: false };
    }
  } else if (freshPlan) {
    if (!freshMerged.isLoading && !freshMerged.isError) {
      fresh = { count: freshMerged.count, atLeast: freshMerged.atLeast || freshMerged.failed > 0 };
    }
  }

  const filtered = Boolean(
    submittedQ || types.length > 0 || platforms.length > 0 || axisKey !== "all",
  );

  /* NO COUNT (2026-09-25). The wall prints no trend count. Its order is the
     Sort facet's to say, and the live region's: best match under a search,
     otherwise the picked sort. */
  const orderSpoken = searching ? "best match" : ORDER_SPOKEN[sort];

  /* One submit for both search fields: the header's, and its repeat in the bar
     once the header has scrolled away. They share `q`, so they are one field
     drawn twice rather than two searches. */
  const submitSearch = () => {
    toFirstPage();
    setSubmittedQ(q);
  };

  const reset = () => {
    setQ("");
    setSubmittedQ("");
    filterActions.clearAll();
  };

  const activeSummary = [
    submittedQ ? `search "${submittedQ}"` : null,
    types.length ? types.map((t) => TYPE_LABEL[t]).join(" or ") : null,
    platforms.length ? platforms.map((p) => platformLabel(p)).join(" or ") : null,
    regionWord(axisKey),
  ]
    .filter((bit): bit is string => bit !== null)
    .join(" · ");

  /* What the live region adds when some of the reads behind this wall did not
     answer. The notice above the wall says the same thing in print, and the two
     may not disagree about how complete the wall is. */
  const incompleteCaveat =
    view.failed > 0
      ? `. ${view.failed} of the ${view.requested} reads behind this view did not answer, so the wall is incomplete`
      : "";

  /* The page, first in the sentence, so a page turn is heard as one. The total
     is said only where it is known: a merged wall that read a window short
     has a floor for a page count, not a count, so there it is just the page. */
  const shownPage = Math.min(page, pageCount);
  const pageSpoken =
    pageCount > 1 ? (view.atLeast ? `Page ${shownPage}. ` : `Page ${shownPage} of ${pageCount}. `) : "";

  /* The same groups the scrolled panel and the phone sheet draw, so the three
     can never disagree about what a filter holds. */
  const filterGroups = trendFilterGroups(filterState, filterActions, searching);
  const filterSummary = trendFilterSummary(filterState);

  const detailModal = (
    <TrendDetailModal
      trendId={selectedTrendId}
      onClose={() => selectTrend(null)}
      preview={preview && selectedTrendId ? preview.detail(selectedTrendId) : undefined}
      nav={isPhone ? undefined : detailNav}
    />
  );

  /* TRENDS ON A PHONE: the feed replaces the header, the bar and the wall. The
     detail opens over it exactly as it does over the wall. */
  if (isPhone) {
    return (
      <MotionConfig reducedMotion="user">
        <PlaybackProvider stageOpen={selectedTrendId !== null}>
          <DashboardPage className="overflow-visible">
            <TrendsPhoneFeed
              rows={phone.rows}
              hasMore={phone.hasMore}
              isLoading={phone.isLoading}
              isFetchingMore={phone.isFetchingMore}
              isError={phone.isError}
              onRetry={phone.refetch}
              failed={phone.failed}
              moreError={phone.moreError}
              onRetryMore={phone.retryMore}
              onLoadMore={loadMorePhone}
              resetKey={JSON.stringify(phoneFirst.requests)}
              filterGroups={filterGroups}
              anyFilter={anyFilterSet(filterState)}
              types={types}
              onClearAll={filterActions.clearAll}
              filtered={filtered}
              onClearEverything={reset}
              query={q}
              onQueryChange={setQ}
              onSubmitSearch={submitSearch}
              onCancelSearch={() => {
                setQ("");
                setSubmittedQ("");
                toFirstPage();
              }}
              lastSeenMs={lastSeenMs}
              detailOpen={selectedTrendId !== null}
              onOpenDetail={selectTrend}
              live={!preview}
            />
            {detailModal}
          </DashboardPage>
        </PlaybackProvider>
      </MotionConfig>
    );
  }

  return (
    /* Motion obeys the system's reduced-motion setting for everything under
       it. */
    <MotionConfig reducedMotion="user">
    {/* The provider wraps the wall AND the detail panel, so one switch and one
        reduced-motion floor govern every video this page can put on screen. An
        open panel holds every wall plate still, the one with sound included. */}
    <PlaybackProvider stageOpen={selectedTrendId !== null}>
    {/* The cockpit's own scroll field owns the scrollbar and the page padding, so
        this view opts out of DashboardPage's internal overflow. A nested scroll
        box here would swallow the filter bar's sticky positioning. */}
    <DashboardPage className="overflow-visible">
      <div ref={rootRef} className="flex w-full flex-col">
        {/* No pause control on Trends (2026-09-26): plates only play under the
            pointer or keyboard focus, so nothing moves at rest to hold still.
            The reduced-motion floor and the stage hold still apply. */}
        <WallViewHeader title="Trends" subhead={SUBHEAD} collapse>
          {/* Only once the route has answered, and only when there is news. It
              sits with the title rather than in the filter row: news is about
              the page, and at 1280 the row has no width to spare for it. */}
          {fresh && fresh.count > 0 && lastSeen ? (
            <Pill>
              {fresh.count}
              {fresh.atLeast ? "+" : ""} new
              <span className="trend-new-since"> since {sinceLabel(lastSeen)}</span>
            </Pill>
          ) : null}
          <WallSearch value={q} onChange={setQ} onSubmit={submitSearch} label="Search trends" />
        </WallViewHeader>

        <WallBar
          condensed={{
            active: condensedActive,
            row: (
              <TrendsScrolledRow
                title="Trends"
                summary={filterSummary}
                panel={
                  <TrendFilterPanel
                    groups={filterGroups}
                    canClear={anyFilterSet(filterState)}
                    onClearAll={filterActions.clearAll}
                  />
                }
                search={
                  <span
                    className="contents"
                    onFocus={() => setRowSearchFocused(true)}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                        setRowSearchFocused(false);
                      }
                    }}
                  >
                    <WallSearch value={q} onChange={setQ} onSubmit={submitSearch} label="Search trends" />
                  </span>
                }
              />
            ),
          }}
          readout={null}
          axes={
            <>
              <WallCaption>Kind</WallCaption>
              <WallRail
                label="Filter by kind"
                options={KIND_OPTIONS}
                picked={types}
                onToggle={filterActions.toggleType}
              />

              <WallHairline />

              <WallCaption>Source</WallCaption>
              <WallRail
                label="Filter by source"
                options={SOURCE_RAIL_OPTIONS}
                picked={platforms}
                onToggle={filterActions.toggleSource}
              />

              <WallHairline />

              <WallFacet
                caption="Region"
                label="Filter by region"
                value={axisKey === "all" ? undefined : axisKey}
                options={REGION_FACET_OPTIONS}
                anyLabel={ANY_REGION.label}
                anyIcon={ANY_REGION.icon}
                onChange={(next) => filterActions.pickRegion(next ?? "all")}
              />

              <WallHairline strong />

              {/* Under a search the vector path orders by relevance and ignores
                  sort, so the facet says so and cannot be changed. */}
              <WallFacet
                caption="Sort"
                label="Sort trends"
                value={sort}
                options={SORT_FACET_OPTIONS}
                isDisabled={searching}
                faceValue={searching ? SEARCH_ORDER_LABEL : sortFaceLabel(sort)}
                restingValue="newest"
                onChange={(next) => filterActions.pickSort(next ?? "newest")}
              />
            </>
          }
        />

        {/* The reading key that used to sit here is gone: two paragraphs of
            layout theory at the top of the wall were read once and then in the
            way of the thing they described. The heading is not gone, it is only
            no longer drawn. Without an h2 the page goes h1 straight to the
            per-card h3s, and the only other h2s live in the error and empty
            branches, which are exactly the states where there is no wall. So it
            stays as the wall region's accessible name: the heading tree still
            reads h1, h2, h3 and a screen reader can still jump to the wall.
            A section, not a main: the cockpit shell already draws the page's
            one main, and a second one nested inside it is a landmark error.
            The heading is also where focus lands after a page turn or Clear
            filters, so it takes focus without joining the tab order. */}
        <section className="pb-16 pt-6" aria-labelledby="trend-wall-heading">
          <h2 id="trend-wall-heading" ref={wallHeadingRef} tabIndex={-1} className="sr-only">
            The wall
          </h2>

          {/* One live region that never unmounts, so every filter and page
              change is announced, not only the first load. Mounting it inside the
              loading branch is why it used to speak exactly once. Debounced,
              because a rail takes several answers now and setting three of them
              in a second used to be three announcements. */}
          {/* Says what the wall now shows, in the readout's own terms: the
              filters and the order, never a count, because the page prints
              none. A wall missing a failed read says so here too rather than
              reading out a confident answer over an incomplete wall. */}
          <WallFilterAnnouncer
            message={
              isLoading
                ? "Reading the trend library"
                : isError
                  ? "The trend feed did not answer, so nothing is shown"
                  : visible.length === 0
                    ? filtered
                      ? `No trends match the current filters: ${activeSummary}${incompleteCaveat}`
                      : "Nothing in the library yet"
                    : filtered
                      ? `${pageSpoken}Showing trends that match the current filters: ${activeSummary}, ${orderSpoken}${incompleteCaveat}`
                      : `${pageSpoken}Showing the trend library, ${orderSpoken}${incompleteCaveat}`
            }
          />

          {/* Above every branch below, because a failed read is exactly as
              relevant to an empty wall as to a full one: "No trends match" while
              one of the reads never answered would be the page asserting an
              answer it does not have. */}
          {view.failed > 0 && !isError && !isLoading ? (
            <WallIncompleteNotice
              failed={view.failed}
              requested={view.requested}
              onRetry={refetch}
              ease="trend-ease"
            />
          ) : null}

          {isLoading ? (
            <SkeletonWall />
          ) : isError ? (
            /* The library's Error state, centred in the space it stands in for. */
            <ErrorState
              minHeight={440}
              title="The feed did not answer"
              body="The trend library could not be read, so nothing is shown. An empty wall here would be a guess, and a guess is worse than a gap."
              onRetry={refetch}
            />
          ) : visible.length === 0 ? (
            filtered ? (
              <FilteredEmptyState
                minHeight={440}
                title="No trends match"
                body="The library answered, and nothing in it fits every filter at once. Trends only change when a discovery run lands, so widening a filter beats waiting."
                activeFilters={[activeSummary]}
                onClear={() => {
                  reset();
                  focusWallTop();
                }}
              />
            ) : (
              <EmptyState
                minHeight={440}
                icon={<Inbox aria-hidden />}
                title="Nothing in the library yet"
                body="The library answered, and it is empty. Nothing has been discovered yet."
              />
            )
          ) : (
            <>
              {/* The field is the thing the wall measures: it carries the
                  container query, so the column count follows the space this
                  view actually has rather than the width of the window. */}
              <div className="co-wall-field">
                {/* The dim is for a RE-READ OF THIS SAME WALL and nothing else:
                    the retry button, or a background re-read after the rows went
                    stale. A search or a filter asks a different question, and
                    that path never reaches here â `trendSingleView` reads the
                    previous question's kept rows as unread, so the wall goes back
                    to the skeleton rather than sitting under a dim the viewer can
                    miss and read as a frozen page. */}
                <div
                  className={cn(
                    "co-wall trend-ease transition-opacity",
                    isFetching && "opacity-60",
                  )}
                >
                  {visible.map((trend, index) => (
                    <TrendCard
                      key={trend.id}
                      trend={trend}
                      size={plateSize(trend, index)}
                      standIn={preview?.standInFor(trend)}
                      onOpen={openTrend}
                      isNew={lastSeenMs !== null && Date.parse(trend.createdAt) > lastSeenMs}
                      live={!preview}
                      fitHue={fitHueOf(trend)}
                      order={index}
                    />
                  ))}
                </div>
              </div>

              {/* The pager, and no count beside it: the wall prints none. */}
              <WallPager
                page={page}
                pageCount={pageCount}
                onPrev={() => {
                  setPage((p) => p - 1);
                  focusWallTop();
                }}
                onNext={() => {
                  setPage((p) => p + 1);
                  focusWallTop();
                }}
              />
            </>
          )}

        </section>
      </div>

      {detailModal}
    </DashboardPage>
    </PlaybackProvider>
    </MotionConfig>
  );
}
